import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import type { FormControl } from '@angular/forms';

import { map, startWith, switchMap } from 'rxjs';

import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { BoxComponent, FormFieldComponent } from '@core/user-interface-legacy';
import { RatingScaleComponent } from '@recruitee/candidate-evaluation-settings-ui';

@Component({
  selector: 'rt-evaluation-rating-field',
  templateUrl: './evaluation-rating-field.component.html',
  styleUrls: ['./evaluation-rating-field.component.less'],
  imports: [
    NgTemplateOutlet,
    BoxComponent,
    TranslatePipe,
    FormFieldComponent,
    RatingScaleComponent,
  ],
})
export class EvaluationRatingFieldComponent {
  public readonly control = input.required<FormControl<number | null>>();
  public readonly ratingScale = input<RatingScale | null>(null);
  public readonly hasBox = input<boolean>(true);
  public readonly showValidationErrors = input<boolean>(false);

  protected value = toSignal(
    toObservable(this.control).pipe(
      switchMap(control => control.valueChanges.pipe(startWith(control.value))),
    ),
    {
      initialValue: null,
    },
  );
  protected isControlInvalid = toSignal(
    toObservable(this.control).pipe(
      switchMap(control =>
        control.events.pipe(
          map(() => control.invalid),
          startWith(control.invalid),
        ),
      ),
    ),
    { initialValue: false },
  );

  protected required = toSignal(
    toObservable(this.control).pipe(
      switchMap(control =>
        control.events.pipe(
          map(() => control.hasValidator(Validators.required)),
          startWith(control.hasValidator(Validators.required)),
        ),
      ),
    ),
    { initialValue: false },
  );

  protected hasError = computed(() => this.showValidationErrors() && this.isControlInvalid());

  protected select(score: number): void {
    const control = this.control();

    control.setValue(control.value === score ? null : score);
    control.markAsDirty();
    control.markAsTouched();
  }
}
